import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { hasBillingRole } from '../utils/roles';
import { Link } from 'react-router-dom';
import { LuRefreshCw, LuFileText, LuCircleAlert } from 'react-icons/lu';
import LoadingSpinner from './LoadingSpinner';
import { toast } from 'react-toastify';
import api from '../services/api';

const FEE_TYPE_LABELS = {
  storage_fee: 'Storage',
  handling_inbound: 'Inbound Handling',
  handling_outbound: 'Outbound Handling',
  receiving_pallet: 'Receiving - Pallet',
  receiving_carton: 'Receiving - Carton',
  serial_capture: 'Serial Number Capture',
  mac_capture: 'MAC Address Capture',
  relabel: 'Relabel Service',
  order_picking: 'Order Picking',
  picking_fee: 'Picking',
  packing_fee: 'Packing',
  shipping_fee: 'Shipping',
  asset_tagging: 'Asset Tagging',
  reboxing: 'Reboxing',
  kitting_fee: 'Kitting',
  assembly_fee: 'Assembly',
  returns_handling: 'Returns Handling',
  labour: 'Labour',
  insurance: 'Insurance'
};

const RequestBillingManager = ({ request, onInvoiceCreated, isAdmin = true }) => {
  // Invoice creation is billing-role gated on the backend (RF15).
  const { user } = useAuth();
  const canMutateBilling = hasBillingRole(user);
  const [billingData, setBillingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const fetchBillingSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const endpoint = isAdmin
        ? `/admin/requests/${request.id}/billing/summary`
        : `/requests/${request.id}/billing/summary`;
      const response = await api.get(endpoint);
      setBillingData(response.data);
    } catch (err) {
      setError('Failed to load billing summary');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, request?.id]);

  const createInvoice = async () => {
    try {
      setProcessing(true);
      const response = await api.post(`/admin/requests/${request.id}/billing/create-invoice`);
      if (response.data.invoice) {
        toast.success('Invoice created');
        if (onInvoiceCreated) onInvoiceCreated(response.data.invoice);
      } else {
        toast.info('No billable items for this request');
      }
      fetchBillingSummary();
    } catch (err) {
      toast.error('Failed to create invoice');
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    if (request?.id) fetchBillingSummary();
  }, [request?.id, fetchBillingSummary]);

  if (loading) return <div className="flex justify-center py-8"><LoadingSpinner /></div>;

  if (error) {
    return (
      <div className="text-center py-8 text-gray-500">
        <LuCircleAlert className="mx-auto h-8 w-8 text-gray-300 mb-2" />
        <p className="text-sm">{error}</p>
        <button onClick={fetchBillingSummary} className="mt-2 text-sm text-indigo-600 hover:text-indigo-800">Retry</button>
      </div>
    );
  }

  if (!billingData) {
    return <div className="text-center py-8 text-sm text-gray-500">No billing data available.</div>;
  }

  const {
    billingItems,
    subtotal,
    totalAmount,
    tax,
    invoice: billingInvoice,
    estimatedStorage,
    invoiceEligible = true,
    isEstimate = false,
    reason
  } = billingData;
  const taxInfo = tax || { name: 'GST', rate: 0.05, amount: (subtotal || 0) * 0.05 };

  // Check for connected invoice from billing data or request associations
  const connectedInvoice = billingInvoice
    || (request?.Invoices && request.Invoices.length > 0 ? request.Invoices[request.Invoices.length - 1] : null);

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div>
      {/* Invoice-style line items */}
      {isEstimate && (
        <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          Estimated outbound charges. The final invoice uses packages actually shipped or picked up.
        </div>
      )}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase w-20">Qty</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase w-24">Rate</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase w-28">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {billingItems && billingItems.length > 0 ? (
              billingItems.map((item, index) => (
                <tr key={index}>
                  <td className="px-6 py-3 text-sm text-gray-900">
                    <div>{item.description}</div>
                    {item.fee_type && (
                      <div className="text-xs text-gray-400">
                        {FEE_TYPE_LABELS[item.fee_type] || item.fee_type.replace(/_/g, ' ')}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-700 text-right">{item.quantity}</td>
                  <td className="px-6 py-3 text-sm text-gray-700 text-right">${(item.rate || 0).toFixed(2)}</td>
                  <td className="px-6 py-3 text-sm font-medium text-gray-900 text-right">${(item.amount || 0).toFixed(2)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">
                  {request?.request_type === 'inbound_manifest'
                    ? 'Inbound charges apply at time of receipt based on the number of pallets received.'
                    : request?.request_type === 'incoming'
                    ? 'Inbound handling fees are calculated at the time of receipt based on the number of pallets and cartons received.'
                    : reason || 'No billable items. The customer may not have a fee schedule configured, or fees may be set to $0.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Totals */}
        {billingItems && billingItems.length > 0 && (
          <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-gray-900 font-medium">${(subtotal || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{taxInfo.name} ({(taxInfo.rate * 100).toFixed(0)}%)</span>
                  <span className="text-gray-900">${(taxInfo.amount || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm font-semibold border-t border-gray-300 pt-2">
                  <span className="text-gray-900">Total</span>
                  <span className="text-gray-900">${(totalAmount || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Invoice status / actions */}
      <div className="mt-6 flex items-center justify-between">
        <div>
          {connectedInvoice ? (
            <div className="flex items-center space-x-3">
              <Link
                to={isAdmin ? `/admin/invoices/${connectedInvoice.id}` : `/customer/invoices/${connectedInvoice.id}`}
                className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-800"
              >
                <LuFileText className="mr-1 h-4 w-4" />
                Invoice {connectedInvoice.invoiceNumber || connectedInvoice.id?.substring(0, 8)}
              </Link>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(connectedInvoice.status)}`}>
                {(connectedInvoice.status || 'pending').charAt(0).toUpperCase() + (connectedInvoice.status || 'pending').slice(1)}
              </span>
            </div>
          ) : billingItems && billingItems.length > 0 ? (
            <p className="text-sm text-gray-500">No invoice created yet</p>
          ) : null}
        </div>
        <div className="flex space-x-3">
          <button
            onClick={fetchBillingSummary}
            disabled={loading}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <LuRefreshCw className="mr-1.5 h-3.5 w-3.5" />
            Refresh
          </button>
          {isAdmin && canMutateBilling && invoiceEligible && !connectedInvoice && billingItems && billingItems.length > 0 && (
            <button
              onClick={createInvoice}
              disabled={processing}
              className="inline-flex items-center px-4 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
            >
              {processing ? 'Creating...' : 'Create Invoice'}
            </button>
          )}
        </div>
      </div>

      {estimatedStorage && (
        <div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 px-4 py-4">
          <h4 className="text-sm font-medium text-blue-900">Estimated Monthly Storage</h4>
          <p className="mt-1 text-sm text-blue-800">
            {estimatedStorage.description}: ${estimatedStorage.amount.toFixed(2)}
          </p>
          <p className="mt-1 text-xs text-blue-700">
            This is an estimate based on your current storage fee schedule. Actual monthly storage invoices are generated after inventory is received into storage.
          </p>
        </div>
      )}

      {billingItems?.some((item) => ['serial_capture', 'mac_capture', 'relabel'].includes(item.fee_type)) && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-4">
          <h4 className="text-sm font-medium text-amber-900">Inbound Receipt Services</h4>
          <p className="mt-1 text-sm text-amber-800">
            These one-time receiving charges were generated from the inbound instructions selected on this ASN or manifest.
          </p>
        </div>
      )}
    </div>
  );
};

export default RequestBillingManager;
